import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, FileText, Loader2, Trash2, Edit, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { getPurposes, createPurpose, updatePurpose, deprecatePurpose } from '@/services/dpdpa';

const CATEGORIES = ['essential', 'functional', 'analytics', 'marketing', 'third_party'];
const DATA_CATEGORIES = ['personal_identifiers', 'contact_information', 'financial_data', 'health_data', 'biometric_data', 'location_data', 'behavioral_data'];

export default function ConsentPurposesPage() {
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', description: '', category: 'functional', data_categories: [], retention_period_days: 365, is_mandatory: false, requires_explicit_consent: true });

  const fetchPurposes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPurposes();
      setPurposes(res.purposes || []);
    } catch (err) {
      toast.error('Failed to load purposes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPurposes(); }, [fetchPurposes]);

  const resetForm = () => {
    setForm({ code: '', name: '', description: '', category: 'functional', data_categories: [], retention_period_days: 365, is_mandatory: false, requires_explicit_consent: true });
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!form.code || !form.name) { toast.error('Code and Name are required'); return; }
    try {
      setSaving(true);
      if (editing) {
        await updatePurpose(editing.id, form);
        toast.success('Purpose updated');
      } else {
        await createPurpose(form);
        toast.success('Purpose created');
      }
      setShowDialog(false);
      resetForm();
      fetchPurposes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save purpose');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ code: p.code, name: p.name, description: p.description || '', category: p.category, data_categories: p.data_categories || [], retention_period_days: p.retention_period_days || 365, is_mandatory: p.is_mandatory || false, requires_explicit_consent: p.requires_explicit_consent !== false });
    setShowDialog(true);
  };

  const handleDeprecate = async (id) => {
    if (!window.confirm('Deprecate this purpose? This will not delete existing consents.')) return;
    try {
      await deprecatePurpose(id);
      toast.success('Purpose deprecated');
      fetchPurposes();
    } catch (err) {
      toast.error('Failed to deprecate purpose');
    }
  };

  const toggleDataCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      data_categories: prev.data_categories.includes(cat) ? prev.data_categories.filter(c => c !== cat) : [...prev.data_categories, cat]
    }));
  };

  const sensitivityColor = (cat) => {
    const map = { essential: 'bg-green-500/10 text-green-700', functional: 'bg-blue-500/10 text-blue-700', analytics: 'bg-violet-500/10 text-violet-700', marketing: 'bg-amber-500/10 text-amber-700', third_party: 'bg-red-500/10 text-red-700' };
    return map[cat] || 'bg-gray-500/10 text-gray-700';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="consent-purposes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Consent Purposes</h1>
          <p className="text-sm opacity-60">Define and manage data processing purposes per DPDPA Section 6</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-purpose-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Purpose
        </Button>
      </div>

      <div className="grid gap-4">
        {purposes.length === 0 ? (
          <Card><CardContent className="p-8 text-center opacity-50"><Shield className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No purposes defined yet. Add a purpose or seed defaults from the dashboard.</p></CardContent></Card>
        ) : purposes.map((p) => (
          <Card key={p.id} data-testid={`purpose-card-${p.code}`} className={p.status === 'deprecated' ? 'opacity-50' : ''}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{p.name}</h3>
                    <Badge variant="outline" className="text-xs">{p.code}</Badge>
                    <Badge className={`text-xs ${sensitivityColor(p.category)}`}>{p.category}</Badge>
                    {p.is_mandatory && <Badge variant="destructive" className="text-xs">Mandatory</Badge>}
                    {p.status === 'deprecated' && <Badge variant="secondary" className="text-xs">Deprecated</Badge>}
                  </div>
                  <p className="text-sm opacity-60 mb-2">{p.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {(p.data_categories || []).map(dc => (
                      <Badge key={dc} variant="secondary" className="text-xs capitalize">{dc.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs opacity-50">
                    <span>Retention: {p.retention_period_days} days</span>
                    <span>Consents: {p.consent_count || 0}</span>
                    <span>DPDPA: {p.dpdpa_section || 'Section 6'}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(p)} data-testid={`edit-purpose-${p.code}`}><Edit className="w-4 h-4" /></Button>
                  {p.status !== 'deprecated' && (
                    <Button variant="ghost" size="icon" onClick={() => handleDeprecate(p.id)} data-testid={`deprecate-purpose-${p.code}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="purpose-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Purpose' : 'Create Consent Purpose'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={e => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} placeholder="e.g. MARKETING_EMAIL" disabled={!!editing} data-testid="purpose-code-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="purpose-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Marketing Communications" data-testid="purpose-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Detailed description for notice" data-testid="purpose-desc-input" />
            </div>
            <div>
              <Label>Data Categories</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DATA_CATEGORIES.map(dc => (
                  <Badge key={dc} variant={form.data_categories.includes(dc) ? 'default' : 'outline'} className="cursor-pointer capitalize text-xs" onClick={() => toggleDataCategory(dc)}>{dc.replace(/_/g, ' ')}</Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Retention (days)</Label>
                <Input type="number" value={form.retention_period_days} onChange={e => setForm(prev => ({ ...prev, retention_period_days: parseInt(e.target.value) || 365 }))} data-testid="purpose-retention-input" />
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_mandatory} onCheckedChange={v => setForm(prev => ({ ...prev, is_mandatory: v }))} data-testid="purpose-mandatory-switch" />
                <Label>Mandatory</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.requires_explicit_consent} onCheckedChange={v => setForm(prev => ({ ...prev, requires_explicit_consent: v }))} data-testid="purpose-explicit-switch" />
                <Label>Explicit Consent Required</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-purpose-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
